import { Sidebar } from '@eppendorf/vnls-react-components';
import { User } from '@eppendorf/vnls-user-tenant-utils';
import { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigation, useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { AuthGuard } from '$shared/auth/auth-guard';
import { getIdTokenPayload } from '$shared/auth/auth-service';
import { buildUserId } from '$shared/auth/build-user-id';
import { useAuthContext } from '$shared/auth/use-auth-context';
import { useAccessControlGuard } from '$shared/custom-hooks/useAccessControlGuard';
import { useCurrentUserId } from '$shared/custom-hooks/useCurrentUserId';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';
import { queryClient } from '$shared/query/query-client';
import { checkPreferencesAndInitializePendo } from '$shared/tracking/pendo';
import { prepopulateCache } from '$shared/utils/react-query.utils';

import { AdditionalFeatureIsEnabled } from '$components/additional-feature-guard/additional-feature-is-enabled';
import { ChatBotProvider } from '$components/chat-bot/chat-bot-context';
import { FeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { Footer } from '$components/footer';
import { Header } from '$components/header/header';
import { Loader } from '$components/loader/loader';
import { SidebarNumberBadge } from '$components/sidebar-badge/sidebar-number-badge';
import { SidecarRouter } from '$components/sidecar-router/sidecar-router';
import {
  SampleSafetyPlusOnly,
  EssentialsOnly,
} from '$components/subscription-guard/sample-safety-plus-only';
import { ChatBot } from '$features/chat-bot/chat-bot';
import {
  EssentialsReportHubUpsellNavItem,
  ReportHubBetaAccessNavItem,
} from '$features/compliance-upsell/report-hub-upsell-nav-item';
import { useUnreadIncidentsCount } from '$features/incidents/incidents.api';
import { createGetUserQuery } from '$features/users/users.api';

import { createSidecarRoutes } from 'src/sidecar-routes';

export async function profileLoader(qc: QueryClient): Promise<null> {
  const idToken = await getIdTokenPayload();
  const currentUserId = await buildUserId(idToken.email, idToken.tenantId);
  return prepopulateCache<User>(qc, createGetUserQuery(currentUserId || ''));
}

export function RootLayout() {
  const [isSidebarExpanded, setIsSidebarExpanded] = useLocalStorage<boolean>(
    'vnls_sidebar_expanded',
    false,
  );

  const navigation = useNavigation();
  const auth = useAuthContext();
  const { t } = useTranslation();

  const canViewDevices = useAccessControlGuard('VIEW_DEVICE_OVERVIEW');
  const canViewIncidents = useAccessControlGuard('VIEW_OVERVIEW');
  const canViewUsers = useAccessControlGuard('VIEW_USER_OVERVIEW');
  const canViewReports = useAccessControlGuard('READ_STABILITY_REPORT');
  const canViewOwnProfile = useAccessControlGuard('VIEW_OWN_PROFILE_DETAILS');
  const unreadIncidentsCount = useUnreadIncidentsCount(canViewIncidents);
  const isEnabled = useAccessControlGuard('VIEW_AUDIT_TRAIL_OVERVIEW');
  const { email, tenantId } = useTokenAttributes(['email', 'tenantId']);
  const currentUserId = useCurrentUserId(email, tenantId);
  const [searchParams] = useSearchParams();
  const canOpenSidecar =
    canViewDevices ||
    (canViewOwnProfile &&
      !!currentUserId &&
      searchParams.get('sidecarRoute') === 'user/profile' &&
      searchParams.get('id') === currentUserId);

  /**
   * Initialize pendo every time user is authenticated as we use tenantId for tracking
   */
  useEffect(() => {
    checkPreferencesAndInitializePendo();
  }, []);

  return (
    <ChatBotProvider>
      {navigation.state === 'loading' ? <Loader /> : <span />}

      <AuthGuard>
        <Sidebar
          isExpanded={isSidebarExpanded ?? false} // Fallback if LocalStorage is not available
          onExpandedChange={setIsSidebarExpanded}
        >
          <Sidebar.Header />
          <Sidebar.Expander />
          <Sidebar.Navigation>
            <Sidebar.Group>
              {canViewDevices && (
                <FeatureIsEnabled feature="DASHBOARD_BETA">
                  <Sidebar.NavItem
                    to="/dashboard"
                    iconName="dashboard"
                    label={t('sidebar.dashboard')}
                  />
                </FeatureIsEnabled>
              )}
              {canViewDevices && (
                <>
                  <Sidebar.NavItem
                    to="/devices"
                    iconName="generic"
                    label={t('sidebar.deviceManagement')}
                  />
                  <Sidebar.NavItem
                    to="/monitoring"
                    iconName="charts"
                    label={t('sidebar.monitoring')}
                  />
                </>
              )}
              {canViewIncidents && (
                <Sidebar.NavItem
                  to="/notifications"
                  iconName="notifications"
                  label={t('sidebar.notifications')}
                  badge={
                    <SidebarNumberBadge number={unreadIncidentsCount.data?.unreadCount} />
                  }
                />
              )}
              {canViewUsers && (
                <Sidebar.NavItem
                  to="/user-management"
                  iconName="users"
                  label={t('sidebar.userManagement')}
                />
              )}
              {isEnabled && (
                <Sidebar.NavItem
                  to="/audit-events"
                  iconName="audit"
                  label={t('sidebar.auditEvents')}
                />
              )}
              {canViewReports && (
                <SampleSafetyPlusOnly>
                  <AdditionalFeatureIsEnabled
                    feature="REPORT_HUB_ACCESS"
                    fallback={<ReportHubBetaAccessNavItem />}
                  >
                    <Sidebar.NavItem
                      to="/report-hub"
                      iconName="records"
                      label={t('sidebar.reportHub')}
                    />
                  </AdditionalFeatureIsEnabled>
                </SampleSafetyPlusOnly>
              )}
              {canViewReports && (
                <EssentialsOnly>
                  <EssentialsReportHubUpsellNavItem />
                </EssentialsOnly>
              )}
            </Sidebar.Group>
          </Sidebar.Navigation>
        </Sidebar>

        <Header onToggleSidebar={() => setIsSidebarExpanded(!isSidebarExpanded)} />
      </AuthGuard>

      <main>
        <Outlet />
        {canViewDevices && <ChatBot />}
        {canOpenSidecar && <SidecarRouter router={createSidecarRoutes(queryClient)} />}
      </main>

      {!auth.isLoading && <Footer />}
    </ChatBotProvider>
  );
}
